import {logger} from 'yougile-util'
import {DataObjectId, Hub, HubUtil} from 'yougile-model'
import {extensionsContainerId} from 'yougile-model'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {ContextObject} from '../context-object'
import {PlatformEvents} from './platform-events'

/**
 * ExtensionEvents
 *
 * Each platform extension has its own hub located in [extensionsContainerId] that contains
 * map data. The extensionEvents object stores event data, where the key is the [eventName]
 * and the value is a timestamp. This allows events to be triggered from either the client
 * or server side. On the client side, these events can be listened to in order to update
 * the UI or perform other tasks.
 *
 * Can trigger PlatformEvents (just if eventName matches reserved platform event name)
 *
 * Server-side: can only trigger events (see [ExtensionEventsService] on the server).
 * Client-side: can listen and trigger events (this class, ExtensionEvents).
 */
export class ExtensionEvents extends ContextObject {
  private static logTag = '[ExtensionEvents]'

  public static override registered: Map<
    DataObjectId,
    Map<string, ExtensionEvents>
  > = new Map()

  private lastKnownEventTimestamp: Map<string, number> = new Map()
  private eventCallbacks: Map<string, Array<() => void>> = new Map()
  private syncListener: number

  public override afterLoad(): void {
    // The configurator scripts have no extensionId, so we skip init for them
    if (!this.context.extensionId) {
      return
    }

    const hub = this.getExtensionHub()

    if (hub?.data.extensionEvents) {
      const eventKeys = Object.keys(hub.data.extensionEvents)

      for (const eventName of eventKeys) {
        this.lastKnownEventTimestamp.set(
          eventName,
          hub.data.extensionEvents[eventName]
        )
      }
    }

    this.syncListener = SyncEvents.addListener(
      Sync.getCurrentSandbox().companyId,
      () => {
        const hub = this.getExtensionHub()
        return hub?.id
      },
      () => this.handleSyncEvent(),
      false
    )

    logger.log(ExtensionEvents.logTag, this.context.extensionId, 'initialized')
  }

  public override beforeUnload(): void {
    // The configurator scripts have no extensionId, so we skip init for them
    if (!this.context.extensionId) {
      return
    }

    this.eventCallbacks.clear()
    this.lastKnownEventTimestamp.clear()
    SyncEvents.removeListener(this.syncListener)

    this.syncListener = 0

    logger.log(
      ExtensionEvents.logTag,
      `disposed (extension: ${this.context.extensionId})`
    )
  }

  private getExtensionHub(): Hub | undefined {
    const box = Sync.getCurrentSandbox()
    const isContainerExists = box.getHub(extensionsContainerId) !== undefined

    if (!this.context.extensionId || !isContainerExists) {
      logger.warn(
        ExtensionEvents.logTag,
        'feature not ready yet',
        '(there is no extensionId in the context or extensions container does not exists)'
      )
      return undefined
    }

    const hub = box.findHubByTitle(
      this.context.extensionId,
      extensionsContainerId,
      true
    )

    if (!hub) {
      logger.warn(ExtensionEvents.logTag, 'extension hub is not ensured yet')
    }

    return hub
  }

  private handleSyncEvent(): void {
    const hub = this.getExtensionHub()

    if (!hub || hub.deleted || !hub.data.extensionEvents) {
      return
    }

    const eventKeys = Object.keys(hub.data.extensionEvents)

    for (const eventName of eventKeys) {
      const updatedTimestamp = hub.data.extensionEvents?.[eventName] ?? 0
      const lastKnownTimestamp =
        this.lastKnownEventTimestamp.get(eventName) ?? 0

      if (updatedTimestamp > lastKnownTimestamp) {
        logger.log(
          ExtensionEvents.logTag,
          `event "${eventName}" is fired`,
          `(extension: ${this.context.extensionId})`
        )

        if (PlatformEvents.isPlatformEvent(eventName)) {
          PlatformEvents.trigger(eventName)
        }

        this.lastKnownEventTimestamp.set(eventName, updatedTimestamp)
        const callbacks = this.eventCallbacks.get(eventName) ?? []

        for (const callback of callbacks) {
          try {
            callback()
          } catch (error) {
            logger.error(
              ExtensionEvents.logTag,
              `event "${eventName}" callback failed with error`,
              error,
              `(extension: ${this.context.extensionId})`
            )
          }
        }
      }
    }
  }

  public on(eventName: string, callback: () => void): () => void {
    const callbacks = this.eventCallbacks.get(eventName) ?? []
    callbacks.push(callback)
    this.eventCallbacks.set(eventName, callbacks)

    return () => {
      this.lastKnownEventTimestamp.delete(eventName)
      this.eventCallbacks.set(
        eventName,
        this.eventCallbacks.get(eventName).filter(cb => cb !== callback)
      )
    }
  }

  public trigger(eventName: string): void {
    logger.log(
      ExtensionEvents.logTag,
      `trigger event "${eventName}"`,
      `(extension: ${this.context.extensionId})`
    )

    const hub = this.getExtensionHub()

    if (hub) {
      HubUtil.cleanupExpiredExtensionEvents(hub)
      HubUtil.setExtensionEventTimestamp(hub, eventName)
      hub.commit()
    }
  }
}
